import { css, type CSSResult } from 'lit';

/**
 * Dark colour-scheme overrides for the self-hosted Swagger-UI rendered on the
 * `/api-docs` page. The vendor `swagger-ui.css` ships a light theme only and
 * hardcodes its colours, so without this layer the documentation stays light
 * under the app's dark scheme.
 *
 * Every rule is gated behind the `fm-swagger-dark` marker class that
 * `<fm-api-docs>` toggles on the `#swagger-ui` container in dark mode. The
 * leading `#swagger-ui` id also lifts specificity above the vendor
 * `.swagger-ui …` rules so the overrides win without `!important`. Colours
 * read the global `--fm-color-*` tokens (defined in `theme.css`, dark values
 * under `:root.wa-dark`), which inherit across the shell shadow boundary, so
 * the palette matches the rest of the dark UI. Semantic colours (HTTP method
 * badges, the green Authorize button) are intentionally left untouched.
 */
export const swaggerDarkTheme: CSSResult = css`
    #swagger-ui.fm-swagger-dark .swagger-ui,
    #swagger-ui.fm-swagger-dark .swagger-ui .info .title,
    #swagger-ui.fm-swagger-dark .swagger-ui .info h1,
    #swagger-ui.fm-swagger-dark .swagger-ui .info h2,
    #swagger-ui.fm-swagger-dark .swagger-ui .info h3,
    #swagger-ui.fm-swagger-dark .swagger-ui .info h4,
    #swagger-ui.fm-swagger-dark .swagger-ui .info h5,
    #swagger-ui.fm-swagger-dark .swagger-ui .info p,
    #swagger-ui.fm-swagger-dark .swagger-ui .info li,
    #swagger-ui.fm-swagger-dark .swagger-ui .info table,
    #swagger-ui.fm-swagger-dark .swagger-ui .opblock-tag,
    #swagger-ui.fm-swagger-dark .swagger-ui .opblock-tag small,
    #swagger-ui.fm-swagger-dark .swagger-ui .opblock .opblock-summary-path,
    #swagger-ui.fm-swagger-dark .swagger-ui .opblock .opblock-summary-path__deprecated,
    #swagger-ui.fm-swagger-dark .swagger-ui .opblock .opblock-summary-description,
    #swagger-ui.fm-swagger-dark .swagger-ui .opblock-description-wrapper,
    #swagger-ui.fm-swagger-dark .swagger-ui .opblock-description-wrapper p,
    #swagger-ui.fm-swagger-dark .swagger-ui .opblock-external-docs-wrapper,
    #swagger-ui.fm-swagger-dark .swagger-ui .opblock-title_normal,
    #swagger-ui.fm-swagger-dark .swagger-ui .opblock-section-header h4,
    #swagger-ui.fm-swagger-dark .swagger-ui .opblock-section-header label,
    #swagger-ui.fm-swagger-dark .swagger-ui .tab li,
    #swagger-ui.fm-swagger-dark .swagger-ui .parameter__name,
    #swagger-ui.fm-swagger-dark .swagger-ui .parameter__type,
    #swagger-ui.fm-swagger-dark .swagger-ui .parameter__in,
    #swagger-ui.fm-swagger-dark .swagger-ui .parameters-col_description,
    #swagger-ui.fm-swagger-dark .swagger-ui .parameters-col_description p,
    #swagger-ui.fm-swagger-dark .swagger-ui .responses-inner h4,
    #swagger-ui.fm-swagger-dark .swagger-ui .responses-inner h5,
    #swagger-ui.fm-swagger-dark .swagger-ui .response-col_status,
    #swagger-ui.fm-swagger-dark .swagger-ui .response-col_links,
    #swagger-ui.fm-swagger-dark .swagger-ui table thead tr td,
    #swagger-ui.fm-swagger-dark .swagger-ui table thead tr th,
    #swagger-ui.fm-swagger-dark .swagger-ui .markdown p,
    #swagger-ui.fm-swagger-dark .swagger-ui .markdown li,
    #swagger-ui.fm-swagger-dark .swagger-ui .renderedMarkdown p,
    #swagger-ui.fm-swagger-dark .swagger-ui .model,
    #swagger-ui.fm-swagger-dark .swagger-ui .model-title,
    #swagger-ui.fm-swagger-dark .swagger-ui section.models h4,
    #swagger-ui.fm-swagger-dark .swagger-ui .dialog-ux .modal-ux-header h3,
    #swagger-ui.fm-swagger-dark .swagger-ui .dialog-ux .modal-ux-content h4,
    #swagger-ui.fm-swagger-dark .swagger-ui .dialog-ux .modal-ux-content p,
    #swagger-ui.fm-swagger-dark .swagger-ui .dialog-ux .modal-ux-content label {
        color: var(--fm-color-text-normal);
    }

    #swagger-ui.fm-swagger-dark .swagger-ui .scheme-container {
        background: var(--fm-color-surface);
        box-shadow: none;
        border-bottom: var(--wa-border-width-s) solid var(--fm-color-border);
    }

    #swagger-ui.fm-swagger-dark .swagger-ui .opblock,
    #swagger-ui.fm-swagger-dark .swagger-ui .opblock.opblock-get,
    #swagger-ui.fm-swagger-dark .swagger-ui .opblock.opblock-post,
    #swagger-ui.fm-swagger-dark .swagger-ui .opblock.opblock-put,
    #swagger-ui.fm-swagger-dark .swagger-ui .opblock.opblock-delete,
    #swagger-ui.fm-swagger-dark .swagger-ui .opblock.opblock-patch,
    #swagger-ui.fm-swagger-dark .swagger-ui .opblock.opblock-head,
    #swagger-ui.fm-swagger-dark .swagger-ui .opblock.opblock-options,
    #swagger-ui.fm-swagger-dark .swagger-ui .opblock.opblock-deprecated {
        background: var(--fm-color-surface);
        box-shadow: none;
    }

    #swagger-ui.fm-swagger-dark .swagger-ui .opblock .opblock-section-header {
        background: var(--fm-color-surface-page);
        box-shadow: none;
    }

    #swagger-ui.fm-swagger-dark .swagger-ui section.models,
    #swagger-ui.fm-swagger-dark .swagger-ui section.models.is-open h4 {
        border-color: var(--fm-color-border);
    }

    #swagger-ui.fm-swagger-dark .swagger-ui section.models .model-container,
    #swagger-ui.fm-swagger-dark .swagger-ui .model-box {
        background: var(--fm-color-surface-page);
    }

    #swagger-ui.fm-swagger-dark .swagger-ui .opblock-tag {
        border-bottom-color: var(--fm-color-border);
    }

    #swagger-ui.fm-swagger-dark .swagger-ui table thead tr td,
    #swagger-ui.fm-swagger-dark .swagger-ui table thead tr th {
        border-bottom-color: var(--fm-color-border);
    }

    #swagger-ui.fm-swagger-dark .swagger-ui input[type="email"],
    #swagger-ui.fm-swagger-dark .swagger-ui input[type="file"],
    #swagger-ui.fm-swagger-dark .swagger-ui input[type="password"],
    #swagger-ui.fm-swagger-dark .swagger-ui input[type="search"],
    #swagger-ui.fm-swagger-dark .swagger-ui input[type="text"],
    #swagger-ui.fm-swagger-dark .swagger-ui textarea,
    #swagger-ui.fm-swagger-dark .swagger-ui select {
        background: var(--fm-color-surface);
        color: var(--fm-color-text-normal);
        border-color: var(--fm-color-border);
    }

    #swagger-ui.fm-swagger-dark .swagger-ui .btn {
        color: var(--fm-color-text-normal);
        border-color: var(--fm-color-border);
    }

    #swagger-ui.fm-swagger-dark .swagger-ui .markdown code,
    #swagger-ui.fm-swagger-dark .swagger-ui .renderedMarkdown code {
        background: var(--fm-color-surface-page);
        color: var(--fm-color-brand-quiet);
    }

    #swagger-ui.fm-swagger-dark .swagger-ui .dialog-ux .modal-ux {
        background: var(--fm-color-surface);
        border-color: var(--fm-color-border);
    }

    #swagger-ui.fm-swagger-dark .swagger-ui .dialog-ux .backdrop-ux {
        background: var(--fm-color-overlay);
    }

    #swagger-ui.fm-swagger-dark .swagger-ui .authorization__btn svg,
    #swagger-ui.fm-swagger-dark .swagger-ui .expand-operation svg,
    #swagger-ui.fm-swagger-dark .swagger-ui .expand-methods svg,
    #swagger-ui.fm-swagger-dark .swagger-ui .opblock-control-arrow svg,
    #swagger-ui.fm-swagger-dark .swagger-ui .models-control svg,
    #swagger-ui.fm-swagger-dark .swagger-ui .model-box-control svg {
        fill: var(--fm-color-text-normal);
    }

    #swagger-ui.fm-swagger-dark .swagger-ui .expand-operation:hover svg,
    #swagger-ui.fm-swagger-dark .swagger-ui .expand-methods:hover svg {
        fill: var(--fm-color-text-quiet);
    }

    #swagger-ui.fm-swagger-dark .swagger-ui .model-toggle {
        filter: invert(1);
    }
`;
